import deleteFile from '@commenda/api/file/deleteFile';
import {
  Button,
  DocViewerComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { FileBasic } from '@commenda/types/src/file/types';
import { ServiceRequestDetails } from '@commenda/types/src/service-request/types';
import { documentName } from '@commenda/utils';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { KeyedMutator } from 'swr';
import { CorporationUploadFileModal } from './modals/CorporationUploadFileModal';

const MultiDocumentPreview = ({
  documentType,
  viewOnly = false,
  onUploadSuccess,
  serviceRequest,
  mutate,
  serviceRequestTaskId,
}: {
  documentType: LegacyDocumentType;
  viewOnly?: boolean;
  onUploadSuccess?: () => void;
  serviceRequest: ServiceRequestDetails;
  mutate: KeyedMutator<unknown>;
  serviceRequestTaskId: number;
}) => {
  const uploadModalVisibility = useToggle();
  const [previewLinks, setPreviewLinks] = useState<
    { id: number; url: string }[]
  >([]);
  const [uploadFile, setUploadFile] = useState<FileBasic>(null);

  // get list of uploaded docs for this subtype
  const uploadedDocuments =
    serviceRequest?.corporation?.corporationDocuments?.filter(
      (d) => d.documentType === documentType
    ) || [];

  // fetch signed URLs whenever list changes
  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const links = await Promise.all(
        uploadedDocuments
          .flatMap((doc) => doc.files || [])
          .map(async (file) => {
            const url = await getSignedUrlByFileId(file.id, null);
            return { id: file.id, url };
          })
      );
      if (mounted) setPreviewLinks(links);
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [JSON.stringify(uploadedDocuments)]);

  const handleDelete = async (fileId: number) => {
    const ok = await deleteFile({ fileId });
    if (ok) await mutate();
  };

  return (
    <div className="flex flex-col gap-4 h-full">
      <CorporationUploadFileModal
        serviceRequest={serviceRequest}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        file={uploadFile}
        serviceRequestTaskId={serviceRequestTaskId}
        setFile={setUploadFile}
        documentType={documentType}
        heading={`Upload Signed ${documentName(documentType)}`}
        onUploadSuccess={async () => {
          await mutate();
          if (onUploadSuccess) onUploadSuccess();
          setUploadFile(null);
        }}
      />
      <div className="flex items-center justify-between">
        <h2 className="txt-extra-large">{documentName(documentType)}</h2>
        {!viewOnly && (
          <Button
            size="sm"
            onClick={uploadModalVisibility.show}
            variant="primary"
          >
            <Icons.Upload01 className="w-4 h-4" /> Add Document
          </Button>
        )}
      </div>

      {previewLinks.length ? (
        <div className="flex flex-col gap-6 h-full">
          {previewLinks.map(({ id, url }) => (
            <div key={id} className="relative">
              {!viewOnly && (
                <Button
                  size="sm"
                  variant="destructive-link"
                  className="absolute right-2 top-2"
                  onClick={() => handleDelete(id)}
                >
                  <Icons.Trash03 className="w-4 h-4" />
                </Button>
              )}
              <DocViewerComponent uri={url} />
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl bg-bg-secondary h-full mt-6 flex flex-col items-center justify-center">
          <div className="flex h-12 w-12 rounded-lg bg-white border border-primary items-center justify-center mb-5">
            <Icons.File05 className="h-5 w-5" />
          </div>
          <div className="mt-4 flex flex-col gap-1">
            <h3 className="txt-medium-bold text-center text-text-primary">
              No document uploaded
            </h3>
            <p className="text-center">
              Once a document has been uploaded, a preview will load here.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiDocumentPreview;
